import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

interface TeacherCharacter {
  name: string;
  personality: string;
}

interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

// Realistic 3D Professor Avatar Component using GLB Model
const Realistic3DAvatar = React.memo(({
  emotion,
  avatarRef
}: {
  emotion: string;
  avatarRef: React.RefObject<THREE.Group | null>;
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const animationRef = useRef<number | null>(null);
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const animationsRef = useRef<THREE.AnimationAction[]>([]);

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup with professional background
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    scene.fog = new THREE.Fog(0x1a1a2e, 2, 15);
    sceneRef.current = scene;

    // Professional camera setup for full frame face and shoulders view
    const camera = new THREE.PerspectiveCamera(
      20, // Narrow FOV for tight face and shoulders framing
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.set(0, 1.8, 2.8); // Camera positioned higher to frame only face and shoulders
    camera.lookAt(0, 1.8, 0); // Look at face level, higher to avoid legs

    // High-quality renderer setup
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
      preserveDrawingBuffer: true,
      logarithmicDepthBuffer: true
    });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.3;
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    rendererRef.current = renderer;
    mountRef.current.appendChild(renderer.domElement);

    // Professional studio lighting setup
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    // Main key light (like a studio light)
    const mainLight = new THREE.DirectionalLight(0xffffff, 1.2);
    mainLight.position.set(2, 4, 3);
    mainLight.castShadow = true;
    mainLight.shadow.mapSize.width = 2048;
    mainLight.shadow.mapSize.height = 2048;
    mainLight.shadow.camera.near = 0.5;
    mainLight.shadow.camera.far = 10;
    mainLight.shadow.camera.left = -3;
    mainLight.shadow.camera.right = 3;
    mainLight.shadow.camera.top = 3;
    mainLight.shadow.camera.bottom = -3;
    scene.add(mainLight);

    // Fill light for softer shadows
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.7);
    fillLight.position.set(-2, 2, 1);
    scene.add(fillLight);

    // Rim light for definition
    const rimLight = new THREE.DirectionalLight(0xffffff, 0.5);
    rimLight.position.set(0, 1, -3);
    scene.add(rimLight);

    // Warm accent light
    const accentLight = new THREE.PointLight(0xffd700, 0.4, 8);
    accentLight.position.set(0, 3, 2);
    scene.add(accentLight);

    // Create professor avatar group
    const avatar = new THREE.Group();
    avatarRef.current = avatar;
    scene.add(avatar);

    // Load GLB model from Sketchfab
    const gltfLoader = new GLTFLoader();

    // Single model path - just use one professor model
    const modelPath = '/models/professor.glb';

    console.log('Attempting to load GLB model from:', modelPath);
    console.log('Make sure your GLB file is in frontend/public/models/');

    gltfLoader.load(
      modelPath,
      (gltf) => {
        console.log('✅ GLB model loaded successfully:', gltf);
        console.log('📊 Model animations:', gltf.animations?.length || 0);

        const model = gltf.scene;

        // Calculate bounding box to understand model size
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        console.log('📏 Model size:', size);

        // Scale the model to fill the frame with face and shoulders
        const maxDimension = Math.max(size.x, size.y, size.z);
        const targetSize = 2.5; // Larger size to fill frame with face and shoulders
        const scale = targetSize / maxDimension;
        model.scale.setScalar(scale);

        // Center the model and position for full frame face and shoulders view
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center);
        model.position.y = -0.3; // Position very low to frame only face and shoulders

        // Comprehensive debugging and positioning
        console.log('🔍 Original model rotation:', model.rotation);
        console.log('🔍 Model bounding box:', box);
        console.log('🔍 Model center:', center);
        console.log('📦 Model bounding box min:', box.min);
        console.log('📦 Model bounding box max:', box.max);
        console.log('📍 Initial position after centering:', model.position);

        // Reset all rotations first
        model.rotation.set(0, 0, 0);
        console.log('🔄 After reset rotation:', model.rotation);

        // Use original GLB pose without any artificial rotations
        console.log('🎯 Loading model in original GLB pose - no artificial rotations');
        model.rotation.set(0, 0, 0); // No rotations - use original pose

        console.log('🔄 Model rotation set to original:', model.rotation);
        console.log('📍 Model position:', model.position);

        // Add model to scene
        avatar.add(model);
        console.log('✅ Model added to avatar group in original pose');

        // Enhanced material processing for better realism
        model.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;

            // Improve materials for better realism
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(mat => {
                  mat.transparent = false;
                  mat.opacity = 1.0;
                  mat.roughness = 0.7;
                  mat.metalness = 0.1;
                  mat.envMapIntensity = 0.5;
                });
              } else {
                child.material.transparent = false;
                child.material.opacity = 1.0;
                child.material.roughness = 0.7;
                child.material.metalness = 0.1;
                child.material.envMapIntensity = 0.5;
              }
            }
          }
        });

        // Play specific animation: Idlev4.2(maya_head)
        console.log('🎯 Loading model with Idlev4.2(maya_head) animation');

        if (gltf.animations && gltf.animations.length > 0) {
          console.log('✅ Found animations:', gltf.animations.map(clip => clip.name));

          const mixer = new THREE.AnimationMixer(model);
          animationsRef.current = [];

          // Log all available animations and morph targets to see what we have
          console.log('📋 All available animations:', gltf.animations.map(clip => clip.name));

          // Check for morph targets (facial expressions)
          model.traverse((child) => {
            if ((child as any).morphTargetDictionary) {
              console.log('🎭 Available morph targets:', Object.keys((child as any).morphTargetDictionary));
            }
          });

          // Find and play the IdleV4.2(maya_head) animation
          const idleAnimation = gltf.animations.find(clip =>
            clip.name === 'IdleV4.2(maya_head)' ||  // Exact match with correct case
            clip.name.toLowerCase().includes('idle') ||
            clip.name.toLowerCase().includes('maya_head') ||
            clip.name.toLowerCase().includes('idlev4.2')
          );

          if (idleAnimation) {
            console.log('✅ Playing animation:', idleAnimation.name);
            const action = mixer.clipAction(idleAnimation);
            action.play();
            animationsRef.current.push(action);

            // Try to apply a subtle hands-down correction
            console.log('🔄 Applying subtle hands-down correction...');
            setTimeout(() => {
              model.traverse((child) => {
                if (child.type === 'Bone' && child.name) {
                  const boneName = child.name.toLowerCase();
                  if (boneName.includes('arm') || boneName.includes('shoulder')) {
                    // Try a very subtle adjustment
                    child.rotation.x += 0.1; // Slight downward adjustment
                    console.log('🔄 Applied subtle adjustment to:', child.name);
                  }
                }
              });
            }, 100); // Small delay to let animation start
          } else {
            console.log('⚠️ Idle animation not found, playing first animation');
            const action = mixer.clipAction(gltf.animations[0]);
            action.play();
            animationsRef.current.push(action);
          }

          // Store mixer for animation updates
          if (mixerRef.current) {
            mixerRef.current = mixer;
          }
        } else {
          console.log('✅ No animations found, using original GLB pose');
        }

        avatar.add(model);

        // Set up animations if they exist
        if (gltf.animations && gltf.animations.length > 0) {
          const mixer = new THREE.AnimationMixer(model);
          mixerRef.current = mixer;

          gltf.animations.forEach((clip, index) => {
            const action = mixer.clipAction(clip);
            animationsRef.current.push(action);
            console.log(`🎬 Animation ${index}:`, clip.name);
          });

          // Play the first animation if available (usually idle/pose animation)
          if (animationsRef.current.length > 0) {
            animationsRef.current[0].play();
            console.log('✅ Playing default animation to avoid T-pose');
          }
        } else {
          console.log('⚠️ No animations found - model may appear in T-pose');
          // Try to apply a natural pose if no animations
          model.traverse((child) => {
            if (child.type === 'Bone') {
              // Reset bone rotations to natural pose
              child.rotation.set(0, 0, 0);
            }
          });
        }
      },
      (progress) => {
        const percent = (progress.loaded / progress.total) * 100;
        console.log(`📥 Loading progress: ${percent.toFixed(1)}%`);
      },
      (error) => {
        console.error('❌ Error loading GLB model:', error);
        console.log('💡 Troubleshooting tips:');
        console.log('1. Make sure your GLB file is in frontend/public/models/');
        console.log('2. Check the file name is: professor.glb');
        console.log('3. If you downloaded from Sketchfab, extract the GLB from the source/ folder');
        console.log('4. Check browser console for network errors');

        // Create a fallback avatar if GLB fails to load
        createFallbackAvatar(avatar);
      }
    );

    // Function to create a fallback avatar
    const createFallbackAvatar = (avatarGroup: THREE.Group) => {
      // Simple fallback - you can customize this
      const geometry = new THREE.BoxGeometry(1, 2, 1);
      const material = new THREE.MeshStandardMaterial({
        color: 0x3498db,
        roughness: 0.7,
        metalness: 0.1
      });
      const fallback = new THREE.Mesh(geometry, material);
      fallback.castShadow = true;
      fallback.position.set(0, 1.0, 0); // Lower position
      fallback.rotation.x = 0; // Natural pose
      avatarGroup.add(fallback);

      console.log('Using fallback avatar - please add your GLB file to public/models/');
    };

    // Professional animation loop with enhanced effects
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);

      const time = Date.now() * 0.001;

      // Update animation mixer
      if (mixerRef.current) {
        mixerRef.current.update(0.016); // 60fps
      }

      // Enhanced emotion-based animations for professor
      if (emotion === 'speaking') {
        // Professor speaking animation
        avatar.rotation.y = Math.sin(time * 6) * 0.02;
        avatar.scale.y = 1 + Math.sin(time * 3) * 0.003;
        // Trigger speaking animation if available
        if (animationsRef.current.length > 1) {
          animationsRef.current[1]?.play();
        }
      } else if (emotion === 'thinking') {
        // Professor thinking pose
        avatar.rotation.y = Math.sin(time * 0.5) * 0.05;
        avatar.scale.y = 1 + Math.sin(time * 2) * 0.002;
        // Trigger thinking animation if available
        if (animationsRef.current.length > 2) {
          animationsRef.current[2]?.play();
        }
      } else if (emotion === 'listening') {
        // Professor listening attentively
        avatar.rotation.y = Math.sin(time * 1.5) * 0.015;
        avatar.scale.y = 1 + Math.sin(time * 2.5) * 0.002;
        // Leaning forward slightly
        avatar.position.z = Math.sin(time * 2) * 0.05;
        // Trigger listening animation if available
        if (animationsRef.current.length > 3) {
          animationsRef.current[3]?.play();
        }
      } else if (emotion === 'happy') {
        // Professor happy/encouraging
        avatar.scale.y = 1 + Math.sin(time * 2) * 0.005;
        avatar.rotation.y = Math.sin(time * 1) * 0.02;
        // Nodding gesture
        avatar.rotation.x = Math.sin(time * 3) * 0.02;
        // Trigger happy animation if available
        if (animationsRef.current.length > 4) {
          animationsRef.current[4]?.play();
        }
      } else if (emotion === 'idle') {
        // Professor idle/calm
        avatar.rotation.y = Math.sin(time * 0.3) * 0.01;
        avatar.scale.y = 1 + Math.sin(time * 1.5) * 0.002;
        // Trigger idle animation if available
        if (animationsRef.current.length > 0) {
          animationsRef.current[0]?.play();
        }
      } else {
        // Fallback animation
        avatar.rotation.y = Math.sin(time * 0.3) * 0.01;
        avatar.scale.y = 1 + Math.sin(time * 1.5) * 0.002;
      }

      // Gentle breathing animation
      avatar.scale.y = 1 + Math.sin(time * 1.5) * 0.002;

      // Subtle head movement
      avatar.rotation.y = Math.sin(time * 0.2) * 0.01;

      renderer.render(scene, camera);
    };

    animate();

    // Handle window resize
    const handleResize = () => {
      if (!mountRef.current || !renderer) return;

      const width = mountRef.current.clientWidth;
      const height = mountRef.current.clientHeight;

      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (mixerRef.current) {
        mixerRef.current.stopAllAction();
      }
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [emotion]);

  return (
    <div
      ref={mountRef}
      className="w-full h-full min-h-[400px] rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
      }}
    />
  );
});

const RoboTeacherPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [teacherCharacter, setTeacherCharacter] = useState<TeacherCharacter>({
    name: 'Alex',
    personality: 'friendly and encouraging'
  });
  const [currentEmotion, setCurrentEmotion] = useState('idle');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<THREE.Group | null>(null);

  // Time limit state
  const [usageInfo, setUsageInfo] = useState<{
    dailyLimitMinutes: number;
    todayUsageSeconds: number;
    remainingSeconds: number;
    isActive: boolean;
    canUse: boolean;
  } | null>(null);


  const [sessionActive, setSessionActive] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load usage info on component mount
  useEffect(() => {
    if (currentUser?.uid) {
      loadUsageInfo();
    }
  }, [currentUser?.uid]);

  const loadUsageInfo = async () => {
    if (!currentUser?.uid) return;
    try {
      const usage = await api.getRoboTeacherUsage(currentUser.uid);
      setUsageInfo(usage);
      if (usage.isActive) {
        setSessionActive(true);
      }
    } catch (error) {
      console.error('Error loading usage info:', error);
    }
  };

  const startSession = async () => {
    if (!currentUser?.uid) return;
    try {
      const result = await api.startRoboTeacherSession(currentUser.uid);
      setSessionActive(true);
      await loadUsageInfo();
    } catch (error: any) {
      if (error.message.includes('Daily limit reached')) {
        setShowLimitModal(true);
      } else {
        console.error('Error starting session:', error);
      }
    }
  };

  const stopSession = async () => {
    if (!currentUser?.uid) return;
    try {
      const result = await api.stopRoboTeacherSession(currentUser.uid);
      setSessionActive(false);
      await loadUsageInfo();
      console.log(`Session ended. Duration: ${result.sessionDurationSeconds}s, Total usage: ${result.totalUsageSeconds}s`);
    } catch (error) {
      console.error('Error stopping session:', error);
    }
  };

  // Auto-stop session when component unmounts
  useEffect(() => {
    return () => {
      if (sessionActive && currentUser?.uid) {
        stopSession();
      }
    };
  }, [sessionActive, currentUser?.uid]);

  // Real-time usage updates with countdown
  useEffect(() => {
    if (!sessionActive || !usageInfo) return;

    const interval = setInterval(async () => {
      try {
        const updatedUsage = await api.getRoboTeacherUsage(currentUser!.uid);
        setUsageInfo(updatedUsage);

        // Check if user has exceeded limit during active session
        if (!updatedUsage.canUse) {
          setSessionActive(false);
          setShowLimitModal(true);
          // Auto-stop the session on the backend
          try {
            await api.stopRoboTeacherSession(currentUser!.uid);
          } catch (error) {
            console.error('Error auto-stopping session:', error);
          }
        } else if (updatedUsage.remainingSeconds <= 30 && updatedUsage.remainingSeconds > 0) {
          // Show warning when less than 30 seconds remaining
          console.log(`⚠️ Warning: Only ${updatedUsage.remainingSeconds} seconds remaining!`);
        }
      } catch (error) {
        console.error('Error updating usage:', error);
      }
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [sessionActive, currentUser?.uid, usageInfo]);

  const generateAIResponse = async (userMessage: string) => {
    try {
      setIsLoading(true);
      setCurrentEmotion('thinking');

      // First, analyze the sentence for correctness
      const sentenceReview = await analyzeSentence(userMessage);

      const apiUrl = process.env.REACT_APP_API_URL || 'https://english-guru-backend.onrender.com';
      const apiKey = process.env.REACT_APP_API_KEY || 'development-api-key';

      const response = await fetch(`${apiUrl}/api/openai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
        },
        body: JSON.stringify({
          message: userMessage,
          robotName: teacherCharacter.name,
          robotGender: 'male',
          personality: teacherCharacter.personality,
          teachingStyle: 'conversational',
          sentenceReview: sentenceReview,
          instructions: `You are an English teacher. Your job is to analyze the student's sentence and provide feedback. Focus on MAJOR grammar issues only - ignore minor punctuation like commas, periods, etc, also ignore correcting sentence in greeting like hi and hello.

CONVERSATION RULES:
1. Be friendly and conversational - respond naturally to greetings and simple expressions
2. Only correct MAJOR structural grammar mistakes
3. Ignore minor issues like punctuation, capitalization, or informal expressions
4. Don't be overly formal - match the student's conversational tone

MAJOR ISSUES TO CORRECT (only these):
- Subject-verb agreement (He go → He goes)
- Missing articles in important contexts (I have car → I have a car)
- Missing auxiliary verbs (I going → I am going)
- Wrong verb forms (I not like → I don't like)
- Question formation (You like coffee? → Do you like coffee?)

DO NOT CORRECT:
- Simple greetings like "hello", "hi", "hello ALEX"
- Informal expressions
- Minor punctuation or capitalization
- Natural conversational phrases

EXAMPLES OF NATURAL RESPONSES:
- Student: "hello" → "Hello! How are you today?"
- Student: "hello ALEX" → "Hello! Nice to meet you. How are you doing?"
- Student: "I going to store" → "Your sentence has a small mistake. The correct form is: 'I am going to the store.' That's a great place to go! What do you need to buy?"
- Student: "I have car" → "Your sentence has a small mistake. The correct form is: 'I have a car.' That's wonderful! What kind of car do you have?"
- Student: "I am going to the store" → "Your sentence is perfect! That's a great place to go! What do you need to buy?"

Be conversational and friendly, not overly formal or strict!`,
          conversationHistory: messages.map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'assistant',
            content: msg.text
          }))
        }),
      });

      const data = await response.json();
      const aiResponse = data.response || data.fallback;

      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        text: aiResponse,
        sender: 'ai',
        timestamp: new Date()
      }]);

      setCurrentEmotion('speaking');
      speakText(aiResponse);
    } catch (error) {
      console.error('Error generating AI response:', error);
      setCurrentEmotion('happy');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to analyze sentence correctness
  const analyzeSentence = async (sentence: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://english-guru-backend.onrender.com';
      const apiKey = process.env.REACT_APP_API_KEY || 'development-api-key';

      const response = await fetch(`${apiUrl}/api/openai/sentence-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
        },
        body: JSON.stringify({
          sentence: sentence
        }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error analyzing sentence:', error);
      // Return default analysis if API fails
      return {
        isCorrect: true,
        grammarScore: 8,
        issues: [],
        suggestions: [],
        correctedSentence: sentence
      };
    }
  };

  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      setIsSpeaking(true);
      setCurrentEmotion('speaking');
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1.2; // Default pitch
      utterance.volume = 0.8;

      // Start lip movement animation
      startLipMovement();

      utterance.onend = () => {
        setIsSpeaking(false);
        setCurrentEmotion('idle');
        stopLipMovement();
      };

      speechSynthesis.speak(utterance);
    }
  };

  // Lip movement functions
  const startLipMovement = () => {
    console.log('🗣️ Starting lip movement...');
    if (avatarRef.current) {
      const model = avatarRef.current.children[0];
      if (model) {
        let animationFound = false;

        // Method 1: Try morph targets (blend shapes) for facial animation
        model.traverse((child) => {
          console.log('🔍 Checking for morph targets:', child.name, 'morphTargetDictionary:', (child as any).morphTargetDictionary);

          if ((child as any).morphTargetDictionary) {
            const morphTargets = (child as any).morphTargetDictionary;
            console.log('🎭 Available morph targets:', Object.keys(morphTargets));

            // Look for mouth/lip related morph targets
            const mouthTargets = Object.keys(morphTargets).filter(key =>
              key.toLowerCase().includes('mouth') ||
              key.toLowerCase().includes('lip') ||
              key.toLowerCase().includes('jaw') ||
              key.toLowerCase().includes('open')
            );

            if (mouthTargets.length > 0) {
              console.log('🗣️ Found mouth morph targets:', mouthTargets);
              animationFound = true;

              // Animate morph targets
              const animateMorph = () => {
                if (isSpeaking) {
                  const time = Date.now() * 0.01;
                  const value = Math.sin(time * 5) * 0.5; // Animate morph target

                  mouthTargets.forEach(target => {
                    const index = morphTargets[target];
                    if ((child as any).morphTargetInfluences) {
                      (child as any).morphTargetInfluences[index] = value;
                      console.log('🗣️ Animating morph target:', target, 'value:', value);
                    }
                  });
                  requestAnimationFrame(animateMorph);
                } else {
                  // Reset morph targets
                  mouthTargets.forEach(target => {
                    const index = morphTargets[target];
                    if ((child as any).morphTargetInfluences) {
                      (child as any).morphTargetInfluences[index] = 0;
                    }
                  });
                }
              };
              animateMorph();
            }
          }
        });

        // Method 2: Try bone animation if no morph targets found
        if (!animationFound) {
          console.log('⚠️ No morph targets found, trying bone animation...');
          model.traverse((child) => {
            console.log('🔍 Checking bone:', child.name, 'type:', child.type);

            if (child.type === 'Bone' && child.name) {
              const boneName = child.name.toLowerCase();
              if (boneName.includes('jaw') || boneName.includes('mouth') || boneName.includes('head')) {
                console.log('🗣️ Found potential jaw/mouth bone:', child.name);
                animationFound = true;
                (child as any).originalRotation = child.rotation.clone();

                const animateJaw = () => {
                  if (isSpeaking) {
                    const time = Date.now() * 0.01;
                    child.rotation.x = Math.sin(time * 5) * 0.2;
                    console.log('🗣️ Animating jaw:', child.name, 'rotation:', child.rotation.x);
                    requestAnimationFrame(animateJaw);
                  } else {
                    if ((child as any).originalRotation) {
                      child.rotation.copy((child as any).originalRotation);
                    }
                  }
                };
                animateJaw();
              }
            }
          });
        }

        if (!animationFound) {
          console.log('⚠️ No animation method found, trying simple head movement...');
          // Fallback: Animate the entire model for a speaking effect
          (model as any).originalRotation = model.rotation.clone();

          const animateHead = () => {
            if (isSpeaking) {
              const time = Date.now() * 0.01;
              model.rotation.y = Math.sin(time * 2) * 0.05; // Very subtle head movement
              console.log('🗣️ Animating head movement, rotation:', model.rotation.y);
              requestAnimationFrame(animateHead);
            } else {
              if ((model as any).originalRotation) {
                model.rotation.copy((model as any).originalRotation);
              }
            }
          };
          animateHead();
        }
      }
    }
  };

  const stopLipMovement = () => {
    if (avatarRef.current) {
      const model = avatarRef.current.children[0];
      if (model) {
        model.traverse((child) => {
          if (child.type === 'Bone' && child.name && child.name.toLowerCase().includes('jaw')) {
            console.log('🗣️ Stopping lip movement for jaw bone:', child.name);
            // Restore original rotation
            if ((child as any).originalRotation) {
              child.rotation.copy((child as any).originalRotation);
            }
          }
        });
      }
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    // Check if user can use robo-teacher
    if (!usageInfo?.canUse) {
      setShowLimitModal(true);
      return;
    }

    // Check if session is active
    if (!sessionActive) {
      try {
        await startSession();
      } catch (error) {
        setShowLimitModal(true);
        return;
      }
    }

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setCurrentEmotion('listening');

    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      text: userMessage,
      sender: 'user',
      timestamp: new Date()
    }]);

    await generateAIResponse(userMessage);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const startVoiceRecognition = () => {
    // Check if user can use robo-teacher
    if (!usageInfo?.canUse) {
      setShowLimitModal(true);
      return;
    }

    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setCurrentEmotion('listening');
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        // Don't set input message - keep text box empty since we're auto-sending
        setCurrentEmotion('happy');

        // Automatically send the message after voice recognition
        setTimeout(() => {
          handleSendMessageWithText(transcript);
        }, 500); // Small delay to ensure state is updated
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setCurrentEmotion('happy');
      };

      recognition.start();
    }
  };

  // Helper function to send message with specific text
  const handleSendMessageWithText = async (messageText: string) => {
    if (!messageText.trim()) return;

    // Check if user can use robo-teacher
    if (!usageInfo?.canUse) {
      setShowLimitModal(true);
      return;
    }

    // Check if session is active
    if (!sessionActive) {
      try {
        await startSession();
      } catch (error) {
        setShowLimitModal(true);
        return;
      }
    }

    // Clean the speech input by removing unwanted punctuation marks
    const cleanedMessage = messageText
      .replace(/[?.,!;:]/g, '') // Remove common punctuation marks
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();

    if (!cleanedMessage) return; // Don't send empty messages

    setCurrentEmotion('listening');

    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      text: cleanedMessage,
      sender: 'user',
      timestamp: new Date()
    }]);

    await generateAIResponse(cleanedMessage);
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-4 sm:space-x-6">
              {/* <div className="text-4xl">👨‍🎓</div> */}
              <div className="text-center">
                <h1 className="text-xl sm:text-3xl font-bold text-white mb-2">{teacherCharacter.name}</h1>
                <p className="text-white/80 text-sm sm:text-lg">Your Personal AI English Learning Professor</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Time Limit Status */}
        {usageInfo && (
          <div className="mb-4 sm:mb-6 bg-white/10 backdrop-blur-lg rounded-2xl p-4 sm:p-6 border border-white/20">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="text-white">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">⏰</span>
                    <span className="font-semibold text-sm sm:text-base">Daily Limit: {usageInfo.dailyLimitMinutes} minutes</span>
                  </div>
                  <div className="text-xs sm:text-sm text-white/80 mt-1">
                    Used: {Math.floor(usageInfo.todayUsageSeconds / 60)}m {usageInfo.todayUsageSeconds % 60}s
                    | Remaining: {Math.floor(usageInfo.remainingSeconds / 60)}m {usageInfo.remainingSeconds % 60}s
                    {sessionActive && (
                      <span className="ml-2 text-yellow-400 animate-pulse">
                        ⏰ Live countdown
                      </span>
                    )}
                    {sessionActive && usageInfo.remainingSeconds <= 30 && usageInfo.remainingSeconds > 0 && (
                      <span className="ml-2 text-red-400 animate-pulse font-bold">
                        ⚠️ Time running out!
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {sessionActive ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-green-400 font-medium text-sm">Session Active</span>
                    <button
                      onClick={stopSession}
                      className="bg-red-500 hover:bg-red-600 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                    >
                      End Session
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                    <span className="text-gray-400 font-medium text-sm">Session Inactive</span>
                    {usageInfo.canUse ? (
                      <button
                        onClick={startSession}
                        className="bg-green-500 hover:bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                      >
                        Start Session
                      </button>
                    ) : (
                      <span className="text-red-400 font-medium text-sm">Daily Limit Reached</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-12">
          {/* 3D Character Section */}
          <div className="lg:col-span-1">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-4 sm:p-6 lg:p-10 h-[400px] sm:h-[500px] lg:h-[700px] flex flex-col items-center justify-center relative overflow-hidden shadow-2xl">
              {/* Professor info - properly contained */}
              <div className="absolute top-4 sm:top-6 left-4 sm:left-6 right-4 sm:right-6 text-center z-10 max-w-full">
                {/* <h3 className="text-white/90 text-lg font-semibold mb-2 truncate">Your Realistic AI Professor</h3> */}
                {/* <p className="text-white/80 text-sm truncate">
                  {teacherCharacter.name} - {teacherCharacter.personality}
                </p> */}
              </div>
              {/* Animated background particles */}
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full animate-pulse"></div>
                <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-blue-300 rounded-full animate-bounce"></div>
                <div className="absolute bottom-1/3 left-1/2 w-1.5 h-1.5 bg-purple-300 rounded-full animate-pulse"></div>
              </div>

              <div className="mb-4 sm:mb-6 relative z-10">
                <h3 className="text-lg sm:text-xl font-semibold text-white text-center mb-4">
                  Your Realistic AI Professor
                </h3>
                {/* <p className="text-white/70 text-sm text-center mb-4">
                  Professor {teacherCharacter.name} - {teacherCharacter.personality}
                </p> */}

                <div className="relative">
                  <Realistic3DAvatar
                    emotion={currentEmotion}
                    avatarRef={avatarRef}
                  />

                  {/* Enhanced Status Indicators */}
                  <div className="absolute top-2 sm:top-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-30">
                    {isLoading && (
                      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium shadow-lg animate-pulse">
                        🤔 Thinking...
                      </div>
                    )}
                    {isSpeaking && (
                      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium shadow-lg animate-pulse">
                        🗣️ Speaking
                      </div>
                    )}
                    {isTyping && (
                      <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium shadow-lg animate-pulse">
                        ⌨️ Typing...
                      </div>
                    )}

                  </div>

                  {/* Emotion indicator */}
                  <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 z-30">
                    <div className="bg-white/20 backdrop-blur-sm text-white px-2 sm:px-3 py-1 rounded-full text-xs font-medium">
                      {currentEmotion === 'speaking' && '🗣️ Speaking'}
                      {currentEmotion === 'thinking' && '🤔 Thinking'}
                      {currentEmotion === 'listening' && '👂 Listening'}
                      {currentEmotion === 'happy' && '😊 Happy'}
                      {currentEmotion === 'idle' && '😌 Idle'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Voice Controls */}
              <div className="flex flex-col space-y-3 sm:space-y-4 mt-auto mb-4 sm:mb-8 w-full max-w-sm z-20 relative">
                <button
                  onClick={startVoiceRecognition}
                  disabled={!usageInfo?.canUse}
                  className={`px-4 sm:px-8 py-3 sm:py-4 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-xl text-sm sm:text-lg ${usageInfo?.canUse
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700'
                    : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                    }`}
                >
                  🎤 Speak to Professor
                </button>
                <button
                  onClick={() => speakText("Hello! I'm here to help you learn English. How can I assist you today?")}
                  disabled={!usageInfo?.canUse}
                  className={`px-4 sm:px-8 py-3 sm:py-4 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-xl text-sm sm:text-lg ${usageInfo?.canUse
                    ? 'bg-gradient-to-r from-green-500 to-blue-600 text-white hover:from-green-600 hover:to-blue-700'
                    : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                    }`}
                >
                  🔊 Test Voice
                </button>
                <div className="text-center mt-2 sm:mt-4">
                  <p className={`text-xs sm:text-sm font-medium ${usageInfo?.canUse ? 'text-white/70' : 'text-red-400'
                    }`}>
                    {usageInfo?.canUse
                      ? 'Your realistic AI professor is ready to help!'
                      : 'Daily limit reached. Come back tomorrow!'
                    }
                  </p>
                </div>
              </div>


            </div>
          </div>

          {/* Chat Section */}
          <div className="lg:col-span-2">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-4 sm:p-6 lg:p-8 h-[400px] sm:h-[500px] lg:h-[700px] flex flex-col shadow-2xl">
              <div className="flex-1 overflow-y-auto mb-4 sm:mb-6 space-y-4 sm:space-y-6 px-2">
                {messages.length === 0 ? (
                  <div className="text-center text-white/90 py-8 sm:py-16">
                    <div className="text-4xl sm:text-7xl mb-4 sm:mb-8">👋</div>
                    <h3 className="text-xl sm:text-3xl font-bold mb-4 sm:mb-6 text-white">Welcome to English Learning!</h3>
                    <p className="text-sm sm:text-xl text-white/80 leading-relaxed">Start a conversation with your AI teacher to practice English.</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[280px] sm:max-w-xs lg:max-w-md px-4 sm:px-6 py-3 sm:py-4 rounded-2xl shadow-lg ${message.sender === 'user'
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                          : 'bg-white/20 text-white backdrop-blur-sm'
                          }`}
                      >
                        <p className="text-sm sm:text-base leading-relaxed">{message.text}</p>
                        <p className="text-xs opacity-70 mt-2 sm:mt-3">
                          {message.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Section */}
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 mt-4 sm:mt-6">
                <div className="flex-1 relative">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={usageInfo?.canUse ? "Type your message here..." : "Daily limit reached. Come back tomorrow!"}
                    disabled={!usageInfo?.canUse}
                    className={`w-full backdrop-blur-sm border rounded-2xl px-4 sm:px-6 py-3 sm:py-4 resize-none focus:outline-none focus:ring-2 focus:border-transparent text-sm sm:text-base shadow-lg ${usageInfo?.canUse
                      ? 'bg-white/20 text-white placeholder-white/70 border-white/30 focus:ring-purple-500'
                      : 'bg-gray-500/20 text-gray-400 placeholder-gray-500 border-gray-500/30 cursor-not-allowed'
                      }`}
                    rows={2}
                  />
                </div>
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isLoading || !usageInfo?.canUse}
                  className={`px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 text-sm sm:text-lg shadow-lg ${usageInfo?.canUse && inputMessage.trim() && !isLoading
                    ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:from-purple-600 hover:to-pink-700'
                    : 'bg-gray-500 text-gray-300 cursor-not-allowed opacity-50'
                    }`}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* Daily Limit Modal */}
      {showLimitModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="text-6xl mb-4">⏰</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Daily Limit Reached</h3>
              <p className="text-gray-600 mb-6">
                You've used your {usageInfo?.dailyLimitMinutes || 2} minutes for today.
                Come back tomorrow for more practice!
              </p>
              <div className="text-sm text-gray-500 mb-6">
                <p>Today's usage: {Math.floor((usageInfo?.todayUsageSeconds || 0) / 60)}m {(usageInfo?.todayUsageSeconds || 0) % 60}s</p>
                <p>Daily limit: {usageInfo?.dailyLimitMinutes || 2} minutes</p>
              </div>
              <button
                onClick={() => setShowLimitModal(false)}
                className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-3 rounded-2xl font-semibold hover:from-purple-600 hover:to-pink-700 transition-all duration-300"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3D Model Attribution */}
      <div className="text-center mt-8 mb-4 text-xs text-white/30 hover:text-white/50 transition-colors duration-300">
        <a
          href="https://skfb.ly/pxUAN"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center space-x-1 bg-black/20 backdrop-blur-sm px-2 py-1 rounded-full inline-flex"
        >
          <span className="text-xs">3D Model:</span>
          <span className="text-xs">"Professor WIT Felipe Boton" by maker.botonlopes</span>
          <span className="text-xs">•</span>
          <span className="text-xs">CC BY 4.0</span>
        </a>
      </div>
    </div>
  );
};

export default RoboTeacherPage; 